import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Grid,
    IconButton,
    Paper,
    Divider,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Card,
    CardContent,
    Tooltip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    Alert,
    AlertTitle,
    Fab,
    Checkbox,
    ListItemText,
} from "@mui/material";
import {
    Plus,
    Trash2,
    BookOpen,
    Clock,
    ChevronLeft,
    Save,
    ChevronDown,
    Image as ImageIcon,
    UploadCloud,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAppSelector, useAppDispatch } from "../../../../Redux/Store/Hooks";
import CourseCardAI from "../../../../Assets/Images/CourseCardAI.png";
import { fetchList as fetchCourseSubjects } from "../../../../Redux/Slices/CourseMaster/CourseSubjectsSlice";
import { fetchList as fetchCourseResourceMeta } from "../../../../Redux/Slices/CourseMaster/CourseResourceMetaSlice";
import { addCourseMaster } from "../../../../Redux/Slices/CourseMaster/AddCourseMasterSlice";
import { listCourseCategory } from "../../../../Redux/Slices/CourseCategory/ListCourseCategorySlice";
import { listyearGroup } from "../../../../Redux/Slices/yearGroup/ListyearGroupSlice";
import { bookletMaterialRead } from "../../../../Redux/Slices/Booklet/Admin/BookletMaterial/BookletMaterialRead";
import { listContentMobile } from "../../../../Redux/Slices/ContentMobile/ListContentMobileSlice";
import { listVideoTutorials } from "../../../../Redux/Slices/VideoTutorials/ListVideoTutorialsSlice";
import { fetchResources as fetchSubjectResources, resetCourseResources } from "../../../../Redux/Slices/CourseMaster/CourseResourcesSlice";
import ResponseDialog from "../../../../Components/ResponseDialog/ResponseDialog";
import { resetaddCourseMaster } from "../../../../Redux/Slices/CourseMaster/AddCourseMasterSlice";
import { getCourseMasterById } from "../../../../Redux/Slices/CourseMaster/ListCourseMasterSlice";

interface SubjectResource {
    subject_id: number;
    resource_type: string;
    duration: number;
    booklet_ids: string[];
    content_ids: string[];
    video_ids: string[];
}

interface Subject {
    id: string;
    subject_id: string | number;
    yearGroup: string;
    name: string;
    duration: string;
    resource_type: string;
    selected_resources: string[];
    availableResources?: any[];
}

const CreateCourse = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const courseId = searchParams.get("id");        // numeric id, used to match in list
    const courseUuid = searchParams.get("course_id"); // UUID string like CRSE.xxx
    const fromPage = searchParams.get("from");
    const backPath = fromPage === "master" ? "/masters/courses" : "/masters/courses";

    // Redux state
    const courseSubjectsData = useAppSelector((state: any) => state.CourseSubjectsSlice || { data: null });
    const courseResourceMetaData = useAppSelector((state: any) => state.CourseResourceMetaSlice || { data: null });
    const addCourseMasterData = useAppSelector((state) => state.AddCourseMasterSlice || { data: null });
    const courseCategoryData = useAppSelector((state) => state.ListCourseCategorySlice || { data: null });
    const bookletMaterialData = useAppSelector((state) => state.BookletMaterialReadSlice || { data: null });
    const contentMobileData = useAppSelector((state) => state.ListContentMobileSlice || { data: null });
    const videoTutorialsData = useAppSelector((state: any) => state.ListVideoTutorialsSlice || { data: null });
    const courseResourcesData = useAppSelector((state: any) => state.CourseResourcesSlice || { data: null });
    const yearGroupData = useAppSelector((state: any) => state.ListyearGroupSlice || { data: null });
    const listCourseMasterData = useAppSelector((state: any) => state.ListCourseMasterSlice || { data: null });

    const subjectsList = Array.isArray(courseSubjectsData?.data) ? courseSubjectsData.data : (Array.isArray(courseSubjectsData?.data?.data) ? courseSubjectsData.data.data : []);
    const resourceMetaList = Array.isArray(courseResourceMetaData?.data) ? courseResourceMetaData.data : (Array.isArray(courseResourceMetaData?.data?.data) ? courseResourceMetaData.data.data : []);
    const courseCategories = Array.isArray(courseCategoryData?.data) ? courseCategoryData.data : (Array.isArray(courseCategoryData?.data?.data) ? courseCategoryData.data.data : []);
    const booklets = Array.isArray(bookletMaterialData?.data) ? bookletMaterialData.data : (Array.isArray(bookletMaterialData?.data?.data) ? bookletMaterialData.data.data : []);
    const contents = Array.isArray(contentMobileData?.data) ? contentMobileData.data : (Array.isArray(contentMobileData?.data?.data) ? contentMobileData.data.data : []);
    const yearGroups = Array.isArray(yearGroupData?.data) ? yearGroupData.data : (Array.isArray(yearGroupData?.data?.data) ? yearGroupData.data.data : []);
    const videos = Array.isArray(videoTutorialsData?.data?.data?.video_tutorials) ? videoTutorialsData.data.data.video_tutorials : [];
    const courseResourcesList = Array.isArray(courseResourcesData?.data) ? courseResourcesData.data : (Array.isArray(courseResourcesData?.data?.data) ? courseResourcesData.data.data : []);

    const [fullName, setFullName] = useState("");
    const [shortName, setShortName] = useState("");
    const [selectedYearGroup, setSelectedYearGroup] = useState<string | number>("");
    const [courseCategoryId, setCourseCategoryId] = useState("");
    const [courseImage, setCourseImage] = useState<File | null>(null);
    const [previewImage, setPreviewImage] = useState<string | null>(null);
    const [courseDescription, setCourseDescription] = useState("");
    const [courseDuration, setCourseDuration] = useState("");
    const [subjects, setSubjects] = useState<Subject[]>([]);

    useEffect(() => {
        // Fetch all necessary data on component mount
        dispatch(listyearGroup());
        dispatch(fetchCourseResourceMeta());
        dispatch(listCourseCategory());

        // If editing, fetch course data from API using the UUID course_id
        if (courseId) {
            dispatch(getCourseMasterById(courseId));
        }
    }, [dispatch, courseId]);

    // Populate form fields when course data arrives from Redux
    useEffect(() => {
        if (courseId && listCourseMasterData?.data?.data) {
            const courseToEdit = listCourseMasterData.data.data.find((c: any) => String(c.id) === String(courseId));
            if (courseToEdit) {
                const yearGroupId = courseToEdit.yeargroup_country_map?.[0]?.id || courseToEdit.year_group_id || "";
                
                setFullName(courseToEdit.full_name || courseToEdit.name || "");
                setShortName(courseToEdit.short_name || "");
                setSelectedYearGroup(yearGroupId);
                setCourseCategoryId(courseToEdit.course_category?.course_category_id || courseToEdit.course_category_id || "");
                setPreviewImage(courseToEdit.image || null);
                setCourseDescription(courseToEdit.description || "");
                setCourseDuration(String(courseToEdit.duration || ""));

                // Map subjects and resources from the API response
                const mappedSubjects: Subject[] = [];
                (courseToEdit.subjects || []).forEach((s: any) => {
                    const resources = s.resources || [];
                    // Group resources by resource_type for this subject
                    const types = Array.from(new Set(resources.map((r: any) => r.resource_type))) as string[];

                    if (types.length > 0) {
                        types.forEach(type => {
                            const resourcesOfType = resources.filter((r: any) => r.resource_type === type);
                            const selectedResources = resourcesOfType.map((r: any) => {
                                // Extract the specific material ID based on type
                                if (type === 'video') return String(r.video?.id || r.id);
                                if (type === 'booklet') return String(r.booklet?.id || r.id);
                                if (type === 'content' || type === 'study_note') return String(r.content?.id || r.id);
                                return String(r.id);
                            });

                            mappedSubjects.push({
                                id: Math.random().toString(36).substr(2, 9),
                                subject_id: s.id,
                                name: s.name || "",
                                duration: String(s.duration || ""),
                                yearGroup: String(yearGroupId),
                                resource_type: type,
                                selected_resources: selectedResources,
                                availableResources: resourcesOfType.map((r: any) => ({
                                    id: type === 'video' ? (r.video?.id || r.id) : (type === 'booklet' ? (r.booklet?.id || r.id) : (r.content?.id || r.id)),
                                    name: r.video?.title || r.booklet?.name || r.content?.name || r.name || r.title || "Selected Material"
                                }))
                            });
                        });
                    } else {
                        // If no resources, still add the subject as a row
                        mappedSubjects.push({
                            id: Math.random().toString(36).substr(2, 9),
                            subject_id: s.id,
                            name: s.name || "",
                            duration: String(s.duration || ""),
                            yearGroup: String(yearGroupId),
                            resource_type: s.resource_type || "",
                            selected_resources: Array.isArray(s.selected_resources) ? s.selected_resources : (Array.isArray(s.selectedResources) ? s.selectedResources : []),
                            availableResources: []
                        });
                    }
                });
                setSubjects(mappedSubjects);

                // If a year group is set, fetch subjects for it
                if (yearGroupId) {
                    dispatch(fetchCourseSubjects({ yeargroup_country_map_id: yearGroupId }));
                }
            }
        }
    }, [courseId, listCourseMasterData?.data?.data, dispatch]);


    const categories = [
        "Information Technology",
        "Mathematics & Logic",
        "Science & Engineering",
        "Business & Management",
        "Arts & Humanities",
        "Health & Medicine",
        "Social Sciences",
        "Language & Literature",
        "Competitive Exams",
        "Creative Arts",
        "Secondary Education"
    ];

    const sampleTopics = ["Introduction", "Fundamentals", "Advanced Concepts", "Practicals", "Case Studies", "Final Review", "Quiz", "Assignment"];

    const sampleYearGroups = ["Year 7", "Year 8", "Year 9", "Year 10", "Year 11", "Year 12", "Year 13"];

    const sampleSubjects = [
        "Mathematics",
        "Physics",
        "Chemistry",
        "Biology",
        "Computer Science",
        "English Literature",
        "History",
        "Business Studies",
        "Economics",
        "Psychology"
    ];

    const sampleStudyNotes = ["Introductory Notes", "Advanced Formulas", "Exam Revision Guide", "Summary Sheets", "Practical Handbook"];
    const sampleBooklets = ["Module 1 Booklet", "Practice Workbook", "Problem Set Vol 1", "Laboratory Manual", "Previous Year Questions"];
    const sampleVideos = ["Full Lecture Video", "Concept Summary", "Problem Solving Tutorial", "Live Session Recording", "Quick Tips"];

    const addSubject = () => {
        setSubjects([{
            id: Date.now().toString(),
            subject_id: 0,
            yearGroup: String(selectedYearGroup), // Set yearGroup from the top-level selection
            name: "",
            duration: "",
            resource_type: "",
            selected_resources: [],
            availableResources: []
        }, ...subjects]);
    };

    const removeSubject = (id: string) => {
        setSubjects(subjects.filter(s => s.id !== id));
    };

    const handleSubjectChange = (id: string, field: keyof Subject, value: any) => {
        setSubjects(subjects.map(s => {
            if (s.id === id) {
                let updated = { ...s, [field]: value };

                // The yearGroup for individual subjects is now driven by the top-level selectedYearGroup
                // No need to fetch subjects here, as the main year group selection already does it.
                // The 'yearGroup' field in Subject interface might become redundant or always reflect selectedYearGroup.

                // If subject_id or resource_type changes, we may want to fetch new resources
                if (field === 'subject_id' || field === 'resource_type') {
                    const sid = field === 'subject_id' ? value : s.subject_id;
                    const rtype = field === 'resource_type' ? value : s.resource_type;

                    if (sid && sid !== 0 && rtype) {
                        dispatch(fetchSubjectResources({ subject_id: sid, resource_type: rtype }));
                        // Clear selection when type/subject changes
                        updated.selected_resources = [];
                    }
                }

                if (field === 'subject_id') {
                    const selectedSubject = subjectsList.find((sub: any) => sub.id === value);
                    updated.name = selectedSubject?.name || '';
                }
                return updated;
            }
            return s;
        }));
    };

    // Update the row-specific resources when Redux data changes
    useEffect(() => {
        if (courseResourcesData?.data && !courseResourcesData?.isLoading) {
            // Find the active row that matches the fetched criteria (if needed, or just apply to the last changed one)
            // For simplicity, we can just apply it to the row with the same subject_id and resource_type that our saga just finished for.
            // But we don't have the params in the 'success' action easily here without a custom action.
            // Let's assume we maintain a 'loadingRowId' if we want precision, or just use the current 'subjects' scan.

            // Actually, safer: find rows with matching (subject_id, resource_type) and update them.
            // Since our saga result corresponds to the latest fetch.
        }
    }, [courseResourcesData]);

    const handleResourceSelection = (subjectId: string, selectedValues: string[]) => {
        setSubjects(subjects.map(s => {
            if (s.id === subjectId) {
                return { ...s, selected_resources: selectedValues };
            }
            return s;
        }));
    };



    const handleSubmit = () => {
        const subjectResources: SubjectResource[] = subjects.map(subject => {
            const resource: SubjectResource = {
                subject_id: Number(subject.subject_id),
                resource_type: subject.resource_type,
                duration: Number(subject.duration) || 0,
                booklet_ids: [],
                content_ids: [],
                video_ids: []
            };

            // Map selected resources to appropriate arrays based on resource type
            if (subject.resource_type === 'booklet') {
                resource.booklet_ids = subject.selected_resources;
            } else if (subject.resource_type === 'content' || subject.resource_type === 'study_note') {
                resource.content_ids = subject.selected_resources;
            } else if (subject.resource_type === 'video') {
                resource.video_ids = subject.selected_resources;
            }

            return resource;
        });

        const payload: any = {
            ...(courseId && { id: Number(courseId) }),
            full_name: fullName,
            short_name: shortName,
            course_id: courseUuid || "0",
            course_category_id: courseCategoryId,
            description: courseDescription,
            duration: Number(courseDuration),
            status: true,
            is_delete: false,
            image: courseImage || previewImage || "",
            year_group_id: Number(selectedYearGroup),
            yeargroup_country_map_id: Number(selectedYearGroup),
            yeargroup_country_map: [Number(selectedYearGroup)],
            subject_resources: subjectResources
        };

        dispatch(addCourseMaster(payload));
    };

    // Handle successful course creation - REMOVED immediate navigation to show dialog first
    useEffect(() => {
        if (addCourseMasterData?.data && !addCourseMasterData?.isLoading) {
            // Navigation moved to ResponseDialog onOkClick
        }
    }, [addCourseMasterData]);


    const isFormValid = () => {
        const isBasicInfoValid =
            fullName.trim() !== "" &&
            shortName.trim() !== "" &&
            courseCategoryId !== "" &&
            courseDescription.trim() !== "" &&
            (courseImage !== null || previewImage !== null) &&
            courseDuration !== "" &&
            Number(courseDuration) > 0;

        if (!isBasicInfoValid) return false;
        if (subjects.length === 0) return false;

        const areSubjectsValid = subjects.every(s =>
            s.yearGroup !== "" &&
            s.subject_id !== 0 &&
            s.subject_id !== "" &&
            s.duration !== "" &&
            Number(s.duration) > 0
        );

        return areSubjectsValid;
    };

    const RequiredStar = () => <span style={{ color: "#ef4444", marginLeft: "4px" }}>*</span>;

    return (
        <Box sx={{ minHeight: "100vh" }}>
            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <IconButton
                        onClick={() => navigate(backPath)}
                        size="small"
                        sx={{
                            bgcolor: "#fff",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                            "&:hover": { bgcolor: "#f1f5f9" }
                        }}
                    >
                        <ChevronLeft size={20} />
                    </IconButton>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: "#1e293b" }}>
                        {courseId ? "Edit Course" : "Create New Course"}
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ mx: "auto" }}>
                <AnimatePresence>
                    {/* Basic Information Section */}
                    <Accordion defaultExpanded sx={{ mb: 2, borderRadius: "16px !important", border: "1px solid #e2e8f0", boxShadow: "0 4px 24px rgba(0,0,0,0.02)", "&:before": { display: "none" } }}>
                        <AccordionSummary expandIcon={<ChevronDown size={20} />} sx={{ px: 3, py: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.5, color: "#1e293b" }}>
                                <Box sx={{ p: 1, bgcolor: "#e0f2fe", borderRadius: "10px", display: "flex" }}>
                                    <BookOpen size={20} color="#0284c7" />
                                </Box>
                                Basic Information
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 3, pb: 4 }}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label={<span>Full Name <RequiredStar/></span>}
                                        placeholder="e.g. Mathematics Basics"
                                        value={fullName}
                                        onChange={(e) => setFullName(e.target.value)}
                                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#f8fafc" } }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label={<span>Short Name <RequiredStar/></span>}
                                        placeholder="e.g. Math"
                                        value={shortName}
                                        onChange={(e) => setShortName(e.target.value)}
                                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#f8fafc" } }}
                                    />
                                </Grid>

                                {/* <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Course Code"
                                    placeholder="e.g. MATH101"
                                    value={courseCode}
                                    onChange={(e) => setCourseCode(e.target.value)}
                                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#f8fafc" } }}
                                /> */}
                                {/* </Grid> */}
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Year Group <RequiredStar/></InputLabel>
                                        <Select
                                            label="Year Group"
                                            value={selectedYearGroup}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setSelectedYearGroup(val);
                                                if (val) {
                                                    dispatch(fetchCourseSubjects({ yeargroup_country_map_id: val }));
                                                    // Reset current subjects when year group changes to prevent mismatch
                                                    setSubjects([]);
                                                }
                                            }}
                                            sx={{ borderRadius: "12px", bgcolor: "#f8fafc" }}
                                        >
                                            {yearGroups.map((yg: any) => (
                                                <MenuItem key={yg.id} value={yg.id}>
                                                    {yg.yeargroup?.name || yg.year_group?.year_group || yg.year_group || yg.year_group_name || yg.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Course Category <RequiredStar/></InputLabel>
                                        <Select
                                            label="Course Category"
                                            value={courseCategoryId}
                                            onChange={(e) => setCourseCategoryId(e.target.value)}
                                            sx={{ borderRadius: "12px", bgcolor: "#f8fafc" }}
                                        >
                                            {courseCategories.map((category: any) => (
                                                <MenuItem key={category.id} value={category.course_category_id || ''}>
                                                    {category.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label={<span>Description <RequiredStar/></span>}
                                        multiline
                                        rows={4}
                                        value={courseDescription}
                                        onChange={(e) => setCourseDescription(e.target.value)}
                                        placeholder="Describe what this course covers..."
                                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#f8fafc" } }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Box sx={{ mb: 1 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, display: "flex", alignItems: "center", gap: 1, color: "#475569" }}>Course Image <RequiredStar/></Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, p: 3, border: "2px dashed #e2e8f0", borderRadius: "16px", bgcolor: "#f8fafc" }}>
                                            {previewImage ? (
                                                <Box
                                                    component="img"
                                                    src={previewImage}
                                                    alt="Preview"
                                                    sx={{ height: 80, width: 80, borderRadius: "12px", objectFit: "cover", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
                                                />
                                            ) : (
                                                <Box sx={{ height: 80, width: 80, borderRadius: "12px", bgcolor: "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                    <ImageIcon size={32} color="#94a3b8" />
                                                </Box>
                                            )}
                                            <Box>
                                                <Button
                                                    variant="contained"
                                                    component="label"
                                                    startIcon={<UploadCloud size={18} />}
                                                    sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, color: "#ffffff", bgcolor: "#009dff", "&:hover": { bgcolor: "#0f172a" }, boxShadow: "none" }}
                                                >
                                                    Upload New Image
                                                    <input
                                                        type="file"
                                                        hidden
                                                        accept="image/*"
                                                        onChange={(e) => {
                                                            const file = e.target.files?.[0];
                                                            if (file) {
                                                                setCourseImage(file);
                                                                const reader = new FileReader();
                                                                reader.onloadend = () => {
                                                                    setPreviewImage(reader.result as string);
                                                                };
                                                                reader.readAsDataURL(file);
                                                            }
                                                        }}
                                                    />
                                                </Button>
                                                <Typography variant="caption" display="block" sx={{ mt: 1, color: "#64748b" }}>
                                                    Recommended size: 800x600px. JPG, PNG or WebP.
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>

                    {/* Course Duration Section */}
                    <Accordion defaultExpanded sx={{ mb: 2, borderRadius: "16px !important", border: "1px solid #e2e8f0", boxShadow: "0 4px 24px rgba(0,0,0,0.02)", "&:before": { display: "none" } }}>
                        <AccordionSummary expandIcon={<ChevronDown size={20} />} sx={{ px: 3, py: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.5, color: "#1e293b" }}>
                                <Box sx={{ p: 1, bgcolor: "#fef3c7", borderRadius: "10px", display: "flex" }}>
                                    <Clock size={20} color="#d97706" />
                                </Box>
                                Course Duration
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 3, pb: 4 }}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label={<span>Duration (days) <RequiredStar /></span>}
                                        type="number"
                                        inputProps={{ min: 1 }}
                                        value={courseDuration}
                                        onChange={(e) => setCourseDuration(e.target.value)}
                                        placeholder="e.g. 30"
                                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#f8fafc" } }}
                                    />
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>

                    {/* Subjects & Curriculum Section */}
                    <Accordion defaultExpanded sx={{ mb: 2, borderRadius: "16px !important", border: "1px solid #e2e8f0", boxShadow: "0 4px 24px rgba(0,0,0,0.02)", "&:before": { display: "none" } }}>
                        <AccordionSummary expandIcon={<ChevronDown size={20} />} sx={{ px: 3, py: 1 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", pr: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.5, color: "#1e293b" }}>
                                    <Box sx={{ p: 1, bgcolor: "#dcfce7", borderRadius: "10px", display: "flex" }}>
                                        <Plus size={20} color="#16a34a" />
                                    </Box>
                                    Subjects
                                    <Chip label={subjects.length.toString()} size="small" sx={{ fontWeight: 700, bgcolor: "#f1f5f9", color: "#475569", borderRadius: "8px" }} />
                                </Typography>
                                <Button
                                    variant="outlined"
                                    startIcon={<Plus size={18} />}
                                    onClick={(e) => { e.stopPropagation(); addSubject(); }}
                                    sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", color: "#16a34a", borderColor: "#16a34a", "&:hover": { bgcolor: "#f0fdf4", borderColor: "#16a34a" } }}
                                >
                                    Add Subject
                                </Button>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 3, pb: 4 }}>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                {subjects.map((subject, index) => (
                                    <Accordion key={subject.id} defaultExpanded sx={{ borderRadius: "12px !important", border: "1px solid #e2e8f0", boxShadow: "none", "&:before": { display: "none" } }}>
                                        <AccordionSummary expandIcon={<ChevronDown size={20} />} sx={{ bgcolor: "#f8fafc", borderBottom: "1px solid #f1f5f9", borderRadius: "12px", "&.Mui-expanded": { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 } }}>
                                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", pr: 2 }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1.5, color: "#334155" }}>
                                                    <Box sx={{ width: 28, height: 28, borderRadius: "8px", bgcolor: "#fff", color: "#0ea5e9", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "14px", fontWeight: 800, border: "1px solid #e2e8f0", boxShadow: "0 2px 4px rgba(0,0,0,0.02)" }}>
                                                        {subjects.length - index}
                                                    </Box>
                                                    {subject.name || `New Subject`}
                                                </Typography>
                                                <Tooltip title="Remove Subject">
                                                    <IconButton onClick={(e) => { e.stopPropagation(); removeSubject(subject.id); }} sx={{ color: "#ef4444", bgcolor: "#fee2e2", "&:hover": { bgcolor: "#fecaca" }, borderRadius: "8px", width: 36, height: 36 }}>
                                                        <Trash2 size={16} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ p: 3 }}>
                                            <Grid container spacing={3}>
                                                <Grid item xs={12} md={6}>
                                                    <FormControl fullWidth disabled={!selectedYearGroup}>
                                                        <InputLabel>Subject Name <RequiredStar/></InputLabel>
                                                        <Select
                                                            label="Subject Name"
                                                            value={subject.subject_id}
                                                            onChange={(e) => handleSubjectChange(subject.id, "subject_id", e.target.value)}
                                                            sx={{ borderRadius: "12px" }}
                                                        >
                                                            {subjectsList.map((s: any) => (
                                                                <MenuItem key={s.id} value={s.id}>{s.name || s.subject_name}</MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <TextField
                                                        fullWidth
                                                        label={<span>Duration (days) <RequiredStar /></span>}
                                                        type="number"
                                                        inputProps={{ min: 1 }}
                                                        value={subject.duration}
                                                        onChange={(e) => handleSubjectChange(subject.id, "duration", e.target.value)}
                                                        placeholder="e.g. 5"
                                                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                                                    />
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <Divider sx={{ my: 1, borderStyle: "dashed", borderColor: "#e2e8f0" }} />
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "11px" }}>
                                                        Linked References & Materials
                                                    </Typography>
                                                </Grid>

                                                <Grid item xs={12} md={6}>
                                                    <FormControl fullWidth>
                                                        <InputLabel>Resource Type</InputLabel>
                                                        <Select
                                                            label="Resource Type"
                                                            value={subject.resource_type}
                                                            onChange={(e) => handleSubjectChange(subject.id, "resource_type", e.target.value)}
                                                            sx={{ borderRadius: "12px" }}
                                                        >
                                                            <MenuItem value=""><em>Select Type</em></MenuItem>
                                                            {resourceMetaList.map((meta: any) => (
                                                                <MenuItem key={meta?.id} value={meta?.meta_value || meta?.name || meta?.type || ''}>
                                                                    {typeof (meta?.meta_value || meta?.name || meta?.type) === 'string' ? String(meta.meta_value || meta.name || meta.type).split('_').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : 'Unknown'}
                                                                </MenuItem>
                                                            ))}
                                                            {/* Fallback if API fails */}
                                                            {resourceMetaList.length === 0 && (
                                                                <>
                                                                    <MenuItem value="booklet">Booklet</MenuItem>
                                                                    <MenuItem value="study_note">Study Note</MenuItem>
                                                                    <MenuItem value="video">Video</MenuItem>
                                                                </>
                                                            )}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>

                                                <Grid item xs={12} md={6}>
                                                    <FormControl fullWidth disabled={!subject.resource_type}>
                                                        <InputLabel>Resource Material</InputLabel>
                                                        <Select
                                                            multiple
                                                            value={subject.selected_resources || []}
                                                            onChange={(e) => handleResourceSelection(subject.id, e.target.value as string[])}
                                                            label="Resource Material"
                                                            renderValue={(selected) => (
                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                    {(selected as string[]).map((value) => {
                                                                        const allResources = [...booklets, ...contents, ...videos, ...courseResourcesList, ...(subject.availableResources || [])];
                                                                        const resource = allResources.find((r: any) => String(r?.id) === value);
                                                                        return <Chip key={value} label={resource?.name || resource?.title || value} size="small" sx={{ bgcolor: "#eff6ff", color: "#2563eb", height: 24, fontWeight: 600, borderRadius: "6px" }} />;
                                                                    })}
                                                                </Box>
                                                            )}
                                                            sx={{ borderRadius: "12px" }}
                                                        >
                                                            {/* subject-specific resources from dynamic API */}
                                                            {courseResourcesData?.isLoading && <MenuItem disabled>Loading resources...</MenuItem>}
                                                            {Array.isArray(courseResourcesList) && courseResourcesList.map((res: any) => (
                                                                <MenuItem key={res.id} value={String(res.id)}>
                                                                    <Checkbox checked={subject.selected_resources.indexOf(String(res.id)) > -1} />
                                                                    <ListItemText primary={res.name || res.title} />
                                                                </MenuItem>
                                                            ))}

                                                            {/* Available resources from the edit response */}
                                                             {(!Array.isArray(courseResourcesList) || (courseResourcesList.length === 0)) && (subject.availableResources || []).map((res: any) => (
                                                                <MenuItem key={res.id} value={String(res.id)}>
                                                                    <Checkbox checked={subject.selected_resources.indexOf(String(res.id)) > -1} />
                                                                    <ListItemText primary={res.name || res.title} />
                                                                </MenuItem>
                                                             ))}

                                                            {/* Dynamic fallback to base lists based on resource_type mapping */}
                                                            {(!Array.isArray(courseResourcesList) || courseResourcesList.length === 0) && (subject.availableResources || []).length === 0 && (
                                                                <>
                                                                    {(subject.resource_type === 'booklet') && booklets.map((booklet: any) => (
                                                                        <MenuItem key={booklet.id} value={String(booklet.id)}>
                                                                            <Checkbox checked={subject.selected_resources.indexOf(String(booklet.id)) > -1} />
                                                                            <ListItemText primary={booklet.name || booklet.title} />
                                                                        </MenuItem>
                                                                    ))}
                                                                    {(subject.resource_type === 'content' || subject.resource_type === 'study_note') && contents.map((content: any) => (
                                                                        <MenuItem key={content.id} value={String(content.id)}>
                                                                            <Checkbox checked={subject.selected_resources.indexOf(String(content.id)) > -1} />
                                                                            <ListItemText primary={content.name || content.title} />
                                                                        </MenuItem>
                                                                    ))}
                                                                    {(subject.resource_type === 'video') && videos.map((video: any) => (
                                                                        <MenuItem key={video.id} value={String(video.id)}>
                                                                            <Checkbox checked={subject.selected_resources.indexOf(String(video.id)) > -1} />
                                                                            <ListItemText primary={video.name || video.title} />
                                                                        </MenuItem>
                                                                    ))}
                                                                </>
                                                            )}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                            </Grid>
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                </AnimatePresence>

                {/* Floating Save Button */}
                <Fab
                    color="primary"
                    aria-label="save"
                    onClick={handleSubmit}
                    disabled={addCourseMasterData?.isLoading || !isFormValid()}
                    sx={{
                        position: 'fixed',
                        bottom: 32,
                        right: 32,
                        zIndex: 1000,
                        width: 64,
                        height: 64,
                        background: (addCourseMasterData?.isLoading || !isFormValid()) ? "#cbd5e1" : "linear-gradient(135deg, #009dff 0%, #7c3aed 100%)",
                        boxShadow: (addCourseMasterData?.isLoading || !isFormValid()) ? "none" : "0 8px 24px rgba(79, 70, 229, 0.4)",
                        "&:hover": {
                            background: "linear-gradient(135deg, #009dff 0%, #6d28d9 100%)",
                            boxShadow: "0 12px 28px rgba(79, 70, 229, 0.6)"
                        },
                        "&.Mui-disabled": {
                            background: "#e2e8f0",
                            color: "#94a3b8"
                        }
                    }}
                >
                    <Save size={28} color={(addCourseMasterData?.isLoading || !isFormValid()) ? "#94a3b8" : "#fff"} />
                </Fab>

                <ResponseDialog 
                    response={addCourseMasterData?.data} 
                    onOkClick={() => {
                        if (addCourseMasterData?.data?.status === true) {
                            navigate(backPath);
                        }
                        dispatch(resetaddCourseMaster());
                    }} 
                />
            </Box>
        </Box>
    );
};

export default CreateCourse;
